import React, {useState} from 'react';
import {Formik, Form, Field, ErrorMessage} from 'formik';
import * as Yup from 'yup';
import './_profile.scss';
import {Link} from 'react-router-dom';
import {routes} from 'common';
import {useDispatch, useSelector} from 'react-redux';
import {authExtraActions} from 'reduxStore/actions';
import ComLoader from 'components/comLoader/ComLoader';
import {toast} from 'react-toastify';
import {IconProfile} from 'assets/icons';

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB in bytes

const validationSchema = Yup.object().shape({
  full_name: Yup.string()
    .required('Full name is required')
    .min(2, 'Full name must be at least 2 characters')
    .max(50, 'Full name must not exceed 50 characters'),
  dob: Yup.date()
    .nullable()
    .max(new Date(), 'Date of birth cannot be in the future'),
  pic: Yup.mixed()
    .nullable()  // ✅ Allows null value
    .test('fileSize', 'File size must be less than 2MB', (value) => {
      if (!value) return true; // Allow empty value
      return value.size <= MAX_FILE_SIZE;
    })
    .test('fileType', 'Unsupported file type', (value) => {
      if (!value) return true; // Allow empty value
      return ['image/jpeg', 'image/png', 'image/gif'].includes(value.type);
    })
});

function Profile() {
  const dispatch = useDispatch();
  const {user, loading} = useSelector((state) => state.auth);

  const [previewUrl, setPreviewUrl] = useState(user.pic || IconProfile);
  const [uploadError, setUploadError] = useState('');

  const initialValues = {
    full_name: user.full_name || '',
    email: user.email,
    phone_number: user.phone_number,
    dob: user.dob,
    pic: null
  };

  const handleSubmit = (values) => {
    const formData = new FormData();
    formData.append('full_name', values.full_name);
    formData.append('dob', values.dob);
    if (values.pic) {
      formData.append('pic', values.pic);
    }
    dispatch(authExtraActions.profilePatch(formData)).then((response) => {
      if (response.type === "auth/profilePatch/fulfilled") {
        toast.success('Profile updated successfully.');
        // TODO: scroll to top
      }
    });
  };

  const handleImageChange = (event, setFieldValue) => {
    const file = event.currentTarget.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setUploadError('File size must be less than 2MB');
        return;
      }
      if (!['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) {
        setUploadError('Only JPEG, PNG and GIF files are allowed');
        return;
      }
      setUploadError('');
      setFieldValue('pic', file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  return (
    <div className="profile-page">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({errors, touched, setFieldValue}) => {
          return (
            <Form className="custom-form">
              <div id="profile" className="profile-avatar-section">
                <div className="profile-avatar-container">
                  <img className="profile-avatar" src={previewUrl} alt="Profile"/>
                  <div className="profile-avatar-overlay">
                    <label htmlFor="pic" className="profile-avatar-upload">
                      <span>Change Photo</span>
                      <input
                        type="file"
                        id="pic"
                        name="pic"
                        accept="image/jpeg,image/png,image/gif"
                        onChange={(e) => handleImageChange(e, setFieldValue)}
                        style={{display: 'none'}}
                      />
                    </label>
                  </div>
                </div>
                {uploadError && <div className="error-message">{uploadError}</div>}
                <div className="profile-fullname">{user.full_name}</div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <Field
                  type="email"
                  id="email"
                  name="email"
                  disabled
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone_number">Phone Number</label>
                <Field
                  type="tel"
                  id="phone_number"
                  name="phone_number"
                  disabled
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label htmlFor="full_name">
                  Full Name <span className="required">*</span>
                </label>
                <Field
                  type="text"
                  id="full_name"
                  name="full_name"
                  className={`form-control ${touched.full_name && errors.full_name ? 'is-invalid' : ''}`}
                />
                <ErrorMessage name="full_name" component="div" className="error-message"/>
              </div>
              <div className="form-group">
                <label htmlFor="dob">
                  Date of Birth <span className="required">*</span>
                </label>
                <Field
                  type="date"
                  id="dob"
                  name="dob"
                  className={`form-control ${touched.dob && errors.dob ? 'is-invalid' : ''}`}
                />
                <ErrorMessage name="dob" component="div" className="error-message"/>
              </div>

              <div className="profile-actions">
                <button className="submit-btn" type="submit" disabled={loading}>
                  {loading ? <ComLoader/> : "Update"}
                </button>
                <Link to={routes.CHANGE_PASSWORD} className="submit-btn">Change Password</Link>
              </div>
            </Form>
          )
        }}
      </Formik>
    </div>
  );
}

export default Profile;